import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Message,
  Result,
  Typography,
  Space
} from '@arco-design/web-react';
import { IconEmail, IconLock, IconCheckCircle, IconInfoCircle } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/useAuth';
import { useOnboardingStatus, useResolveOnboarding, useStartOnboarding } from './hooks/useOnboardingData';
import PublicLayout from '@/modules/public/registration/PublicLayout';
import ErrorBoundary from '@/modules/core/error-handling/ErrorBoundary';

const FormItem = Form.Item;
const { Text } = Typography;

const OnboardingPublicPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('onboarding');
  const { t: tAuth } = useTranslation('auth');
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [resolveData, setResolveData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const launchProgramId = searchParams.get('launch_program_id');

  // Check if logged-in user should be redirected
  const { data: existingStatus, error: statusError } = useOnboardingStatus({
    enabled: !!user
  });

  useEffect(() => {
    if (!user) return;
    if (statusError) {
      navigate('/dashboard');
      return;
    }
    if (!existingStatus) return;
    if (existingStatus.status === 'draft') {
      navigate('/onboarding/wizard');
    } else {
      navigate('/onboarding/status');
    }
  }, [user, existingStatus, statusError, navigate]);

  // Mutations
  const resolveMutation = useResolveOnboarding();
  const startMutation = useStartOnboarding();

  useEffect(() => {
    if (resolveData?.state !== 'approved_login') return;
    const timer = setTimeout(() => {
      navigate('/login');
    }, 3000);
    return () => clearTimeout(timer);
  }, [resolveData, navigate]);

  const isResolveState = (state) => resolveData?.state === state;

  const canRetryEmail = useMemo(() => {
    return resolveData && [
      'no_invitation',
      'invitation_expired',
      'rejected',
      'no_submission'
    ].includes(resolveData.state);
  }, [resolveData]);

  const handleResolve = async (values = {}) => {
    const targetEmail = values.email || email;
    if (!targetEmail) {
      Message.error(tAuth('validation.enterValidEmail'));
      return;
    }

    const payload = { email: targetEmail };
    if (launchProgramId) {
      payload.launch_program_id = parseInt(launchProgramId, 10);
    }

    resolveMutation.mutate(payload, {
      onSuccess: (response) => {
        const data = response?.data;
        if (!data?.state) {
          Message.error(tAuth('errors.invalidServerResponse'));
          setResolveData(null);
          return;
        }
        setEmail(targetEmail);
        setResolveData(data);
        if (data.state === 'approved_login' && data.redirect_path) {
          Message.info(t('public.redirectingToLogin'));
        }
      },
      onError: () => {
        Message.error(tAuth('errors.emailValidationFailed'));
        setResolveData(null);
      }
    });
  };

  const handleStartOnboarding = async (values) => {
    const startPayload = {
      email,
      password: values.password
    };
    if (launchProgramId) {
      startPayload.launch_program_id = parseInt(launchProgramId, 10);
    }

    startMutation.mutate(startPayload, {
      onSuccess: async (response) => {
        const data = response?.data;
        if (data?.success) {
          Message.success(t('public.accountCreated'));
          const loginResult = await login(email, values.password);
          if (loginResult?.success) {
            navigate('/onboarding/wizard');
          } else {
            setResolveData({
              state: 'login_to_wizard',
              message: t('public.accountCreatedLogin'),
              redirect_path: '/onboarding/wizard'
            });
          }
        } else {
          Message.error(data?.message || tAuth('errors.couldNotCreateAccount'));
        }
      },
      onError: () => {
        Message.error(tAuth('errors.couldNotCreateAccount'));
      }
    });
  };

  const handleLoginAndRedirect = async (values) => {
    setActionLoading(true);
    try {
      const result = await login(email, values.password);
      if (result?.success) {
        const redirectPath = resolveData?.redirect_path || '/onboarding/wizard';
        navigate(redirectPath);
      } else {
        Message.error(result?.error || tAuth('errors.wrongPassword'));
      }
    } catch (error) {
      console.error('Error logging in:', error);
      Message.error(tAuth('errors.loginFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = () => {
    setResolveData(null);
  };

  const loading = resolveMutation.isPending;
  const isDirectEnrollment = !!launchProgramId;
  const isReturningUser = resolveData && (isResolveState('login_to_wizard') || isResolveState('login_to_status'));

  const pageTitle = isDirectEnrollment ? t('public.enrollment') : t('public.welcome');
  const pageSubtitle = isReturningUser
    ? (resolveData.message || t('public.hasAccount'))
    : isDirectEnrollment
      ? t('public.enterEmailEnrollment')
      : t('public.enterEmail');

  return (
    <ErrorBoundary>
      <PublicLayout>
        <div className="public-card">
          <div className="public-card-header">
            <h1>{pageTitle}</h1>
            <p>{pageSubtitle}</p>
          </div>

          {!resolveData && (
            <Form layout="vertical" onSubmit={handleResolve}>
              <FormItem
                label={t('public.personalEmail')}
                field="email"
                rules={[
                  { required: true, message: tAuth('validation.emailRequired') },
                  { type: 'email', message: tAuth('validation.emailInvalidFormat') }
                ]}
              >
                <Input
                  prefix={<IconEmail />}
                  placeholder={t('public.emailPlaceholder')}
                  size="large"
                  disabled={loading}
                  autoComplete="email"
                />
              </FormItem>

              <Button type="primary" size="large" long loading={loading} htmlType="submit">
                {t('public.continue')}
              </Button>
            </Form>
          )}

          {resolveData && (
            <div>
              <div style={{ marginBottom: 16, padding: '8px 12px', background: 'var(--color-fill-2)', borderRadius: 4 }}>
                <Space>
                  <IconEmail style={{ color: '#667eea' }} />
                  <Text bold>{email}</Text>
                  <Button type="text" size="mini" onClick={handleReset}>{t('public.change')}</Button>
                </Space>
              </div>

              {isResolveState('needs_password') && (
                <Form layout="vertical" onSubmit={handleStartOnboarding}>
                  <input type="hidden" name="email" autoComplete="email" value={email} />
                  <FormItem
                    label={t('public.createPassword')}
                    field="password"
                    extra={<Text type="secondary" style={{ fontSize: 12 }}>{tAuth('validation.passwordRules')}</Text>}
                    rules={[
                      { required: true, message: tAuth('validation.passwordRequired') },
                      { minLength: 8, message: tAuth('validation.passwordMinLength8') },
                      {
                        validator: (value, callback) => {
                          if (!value) {
                            callback();
                            return;
                          }
                          if (!/[A-Z]/.test(value)) {
                            callback(tAuth('validation.passwordMissingUppercase'));
                          } else if (!/[a-z]/.test(value)) {
                            callback(tAuth('validation.passwordMissingLowercase'));
                          } else if (!/[0-9]/.test(value)) {
                            callback(tAuth('validation.passwordMissingNumber'));
                          } else {
                            callback();
                          }
                        }
                      }
                    ]}
                  >
                    <Input.Password
                      prefix={<IconLock />}
                      placeholder={t('public.securePlaceholder')}
                      size="large"
                      autoComplete="new-password"
                    />
                  </FormItem>

                  <Button type="primary" size="large" long loading={startMutation.isPending} htmlType="submit">
                    {t('public.createAndContinue')}
                  </Button>
                </Form>
              )}

              {(isResolveState('login_to_wizard') || isResolveState('login_to_status')) && (
                <Form layout="vertical" onSubmit={handleLoginAndRedirect}>
                  <input type="hidden" name="email" autoComplete="email" value={email} />
                  <FormItem
                    label={t('public.passwordLabel')}
                    field="password"
                    rules={[{ required: true, message: tAuth('validation.passwordRequired') }]}
                  >
                    <Input.Password
                      prefix={<IconLock />}
                      placeholder={t('public.passwordPlaceholder')}
                      size="large"
                      autoComplete="current-password"
                    />
                  </FormItem>

                  <Button type="primary" size="large" long loading={actionLoading} htmlType="submit">
                    {t('public.continue')}
                  </Button>
                </Form>
              )}

              {isResolveState('approved_login') && (
                <Result
                  status="success"
                  icon={<IconCheckCircle style={{ fontSize: 64, color: '#00b42a' }} />}
                  title={t('public.registrationComplete')}
                  subTitle={
                    resolveData.institutional_email
                      ? t('public.accessWith', { email: resolveData.institutional_email })
                      : t('public.registrationDone')
                  }
                  extra={
                    <Button type="primary" size="large" onClick={() => navigate('/login')}>
                      {t('public.goToLogin')}
                    </Button>
                  }
                />
              )}

              {isResolveState('invitation_expired') && (
                <Result
                  status="warning"
                  icon={<IconInfoCircle style={{ fontSize: 64, color: '#ff7d00' }} />}
                  title={t('public.invitationExpired')}
                  subTitle={resolveData.message}
                />
              )}

              {isResolveState('rejected') && (
                <Result
                  status="error"
                  icon={<IconInfoCircle style={{ fontSize: 64, color: '#f53f3f' }} />}
                  title={t('public.applicationRejected')}
                  subTitle={resolveData.message}
                />
              )}

              {isResolveState('no_invitation') && (
                <Result
                  status="info"
                  icon={<IconInfoCircle style={{ fontSize: 64, color: '#86909c' }} />}
                  title={t('public.emailNotRegistered')}
                  subTitle={t('public.emailNotRegisteredDesc')}
                />
              )}

              {isResolveState('no_submission') && (
                <Result
                  status="info"
                  icon={<IconInfoCircle style={{ fontSize: 64, color: '#86909c' }} />}
                  title={t('public.noActiveApplication')}
                  subTitle={resolveData.message}
                />
              )}

              {canRetryEmail && (
                <Button style={{ marginTop: 16 }} long onClick={handleReset}>
                  {t('public.tryAnotherEmail')}
                </Button>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, textAlign: 'center', color: '#86909c', fontSize: 14 }}>
            <Link to="/login" style={{ color: '#667eea', fontWeight: 600 }}>
              {tAuth('login.submit')}
            </Link>
          </div>
        </div>
      </PublicLayout>
    </ErrorBoundary>
  );
};

export default OnboardingPublicPage;
